import { useModal } from 'modals';
import { Link } from 'react-router';

import LazyVideo from '@cartwhl/ui/atoms/LazyVideo';
import Text from '@cartwhl/ui/base/Text';

import ContentButton from 'components/ContentButton';
import { products } from 'components/ProductPages';
import SectionHeader from 'components/SectionHeader';
import { CDN } from 'consts';

import TestimonialsSection from './TestimonialsSection';

const productVideos: Record<string, string> = {
  'api-access': `${CDN}01_home_page/03_platform_capabilities_section/01_api_update.webm`,
  'comic-ai-video-motion-capture': `${CDN}01_home_page/03_platform_capabilities_section/02_comic_update.webm`,
  'swing-motion-generation': `${CDN}v2_homepage_platform_capabilities_03_swing.webm`,
  'hermes-smart-in-between': `${CDN}01_home_page/03_platform_capabilities_section/04_hermes_update.webm`,
  mascot: `${CDN}mascot/00_home_dribble.webm`,
  'pose-render': `${CDN}v2_homepage_platform_capabilities_05_pose.webm`,
  'character-generation': `${CDN}v2_homepage_platform_capabilities_06_character_gen_v2.webm`,
  'motion-library': `${CDN}v2_homepage_platform_capabilities_07_motion_library.webm`,
  cartographer: `${CDN}cartographer/00_home_run_output.webm`,
};

export default function ProductsSection() {
  const { open } = useModal();

  return (
    <>
      <section id="products" className="py-(--vertical-spacing)">
        <div className="container">
          <SectionHeader
            title="Platform Capabilities"
            description="Cartwheel’s production-ready models power a full range of capabilities to create, generate, reshape, and reuse motion.  Access them directly in Cartwheel Canvas, or integrate via API for scalable production workflows."
          />

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {products.map((product) => {
              const videoSrc = productVideos[product.slug];
              const isComingSoon = product.comingSoonText !== '';
              const containerClassName = `bg-gray-15 group relative flex flex-col rounded-2xl px-8 py-6 transition-shadow aspect-416/640 cursor-pointer hover:shadow-menu`;

              const content = (
                <>
                  {videoSrc && (
                    <LazyVideo
                      src={videoSrc}
                      className="absolute inset-0 h-full w-full rounded-[inherit]"
                      mode="autoplay"
                    />
                  )}

                  <div className="relative z-10 flex flex-1 flex-col items-start justify-between gap-6">
                    <div className="flex flex-col gap-5">
                      <Text
                        variant="web/body/medium/md"
                        lg:variant="web/body/medium/24"
                        className={
                          product.useLightText
                            ? 'text-white/60'
                            : 'text-tertiary'
                        }
                      >
                        {product.name}
                      </Text>
                      <Text
                        variant="web/body/medium/lg"
                        lg:variant="web/body/medium/xl"
                        className={`whitespace-pre-line ${product.useLightText ? 'text-white' : 'text-gray-90'}`}
                      >
                        {product.title}
                      </Text>
                    </div>
                    <ContentButton
                      comingSoon={isComingSoon}
                      comingSoonText={product.comingSoonText}
                      excitedAbout={product.slug}
                    >
                      {isComingSoon ? product.comingSoonText : 'Learn more'}
                    </ContentButton>
                  </div>
                </>
              );

              return isComingSoon ? (
                <div
                  key={product.title}
                  className={containerClassName}
                  onClick={() =>
                    open('NOTIFY_ME', { excitedAbout: product.slug })
                  }
                >
                  {content}
                </div>
              ) : (
                <Link
                  key={product.title}
                  to={`/product/${product.slug}`}
                  className={containerClassName}
                >
                  {content}
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <TestimonialsSection />
    </>
  );
}
